import * as React from "react";
import {
  getPrivilegeInterestsOfPersonaGrata,
  getPrivilegeInterestsOfPersonaNonGrata,
} from "@vingle/wtf-sdk/actions/userProfile";
import PrivilegeInterestComponent from "./component";
import { useStoreState, useActions } from "app/store";
import { useCancelToken } from "@vingle/wtf-sdk/helpers/hook";
import { useIntl } from "react-intl";
import { DeskingPrivilege } from "@vingle/wtf-sdk/enums";

interface IProps {
  type: Vingle.Enums.DeskingPrivilege;
}

function usePrivilegeInterests(props: IProps) {
  const intl = useIntl();
  const {
    privilegeInterestsOfPersonaGrata,
    privilegeInterestsOfPersonaNonGrata,
    isLoading,
  } = useStoreState(state => ({
    privilegeInterestsOfPersonaGrata:
      state.userProfile.privilegeInterests.personaGrata,
    privilegeInterestsOfPersonaNonGrata:
      state.userProfile.privilegeInterests.personaNonGrata,
    isLoading: state.userProfile.meta.isLoadingPrivilegeInterests,
  }));
  const {
    dispatchGetPrivilegeInterestsOfPersonaGrata,
    dispatchGetPrivilegeInterestsOfPersonaNonGrata,
  } = useActions({
    dispatchGetPrivilegeInterestsOfPersonaGrata: getPrivilegeInterestsOfPersonaGrata,
    dispatchGetPrivilegeInterestsOfPersonaNonGrata: getPrivilegeInterestsOfPersonaNonGrata,
  });
  const cancelToken = useCancelToken();
  const { name, privilegeInterests, getPrivilegeInterests } =
    props.type === DeskingPrivilege.PG
      ? {
          name: "pg",
          privilegeInterests: privilegeInterestsOfPersonaGrata,
          getPrivilegeInterests: dispatchGetPrivilegeInterestsOfPersonaGrata,
        }
      : {
          name: "png",
          privilegeInterests: privilegeInterestsOfPersonaNonGrata,
          getPrivilegeInterests: dispatchGetPrivilegeInterestsOfPersonaNonGrata,
        };
  const descriptionMessage = intl.formatMessage({
    id: `account.privilege.${name}.description`,
  });
  const emptyMessage = intl.formatMessage({
    id: `account.privilege.${name}.empty`,
  });
  const handleLoadMore = React.useCallback(() => {
    if (!isLoading || privilegeInterests.data.length === 0) {
      getPrivilegeInterests({
        cancelToken: cancelToken.current.token,
        after: privilegeInterests.paging.after,
      });
    }
  }, [privilegeInterests.paging, isLoading]);
  React.useEffect(() => {
    handleLoadMore();
  }, [props.type]);
  return {
    descriptionMessage,
    emptyMessage,
    handleLoadMore,
    privilegeInterests,
    isLoading,
  };
}

const PrivilegeInterests = React.memo((props: IProps) => {
  const {
    privilegeInterests,
    handleLoadMore,
    isLoading,
    descriptionMessage,
    emptyMessage,
  } = usePrivilegeInterests(props);
  return (
    <PrivilegeInterestComponent
      privilegeInterests={privilegeInterests}
      onLoadMorePrivilegeInterests={handleLoadMore}
      descriptionMessage={descriptionMessage}
      emptyMessage={emptyMessage}
      isLoading={isLoading}
    />
  );
});

export const PrivilegeInterestsOfPersonaGrata = React.memo(() => (
  <PrivilegeInterests type={DeskingPrivilege.PG} />
));
export const PrivilegeInterestsOfPersonaNonGrata = React.memo(() => (
  <PrivilegeInterests type={DeskingPrivilege.PNG} />
));
